import React, { useEffect, useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Loader2,
  Building2,
  Check,
  XCircle,
  ArrowLeft,
  ArrowRight,
  Shield,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { clearActiveClinicId } from '@/contexts/ClinicContext';
import {
  clinicOnboardingService,
  ClinicOnboardingInviteDetails,
} from '@/services/clinicOnboardingService';
import { clinicOnboardingBillingService } from '@/services/clinicOnboardingBillingService';
import { SetPasswordStep, AcknowledgementStep, BillingStep, StripeOnboardingStep } from './clinic-onboarding/steps';
import { PARTIAL_ONBOARDING_STEPS } from './clinic-onboarding/stepRegistry';
import { getFooterText } from '@/config/version';

const PartialClinicOnboarding: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { login } = useAuth();
  const token = searchParams.get('token');
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [validationError, setValidationError] = useState('');
  const [inviteDetails, setInviteDetails] = useState<ClinicOnboardingInviteDetails | null>(null);
  const [onboardingToken, setOnboardingToken] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isAcknowledged, setIsAcknowledged] = useState(false);
  const setPasswordRef = useRef<{ triggerValidation: () => Promise<boolean> }>(null);

  const skipPassword = inviteDetails?.existingUser?.hasPassword === true;
  const visibleSteps = skipPassword
    ? PARTIAL_ONBOARDING_STEPS.filter(s => s.id !== 'set-password')
    : PARTIAL_ONBOARDING_STEPS;

  useEffect(() => {
    const validate = async () => {
      if (!token) {
        setValidationError('No invitation token. Please use the link from your email.');
        setIsValidating(false);
        return;
      }
      try {
        const response = await clinicOnboardingService.validateInvite(token);
        if (!response.success || response.data.onboardingType !== 'partial') {
          setValidationError('This link is for completing an onboarding started by an admin. Use the link from your email.');
          setIsValidating(false);
          return;
        }
        setInviteDetails(response.data);
        setIsValidating(false);
      } catch (err: any) {
        setValidationError(err?.response?.data?.error?.message || 'Invalid or expired link.');
        setIsValidating(false);
      }
    };
    validate();
  }, [token]);

  const runAutoAuth = useCallback(async () => {
    const email = inviteDetails?.email;
    if (!email || !password) {
      navigate('/login', { state: { email: email || '' } });
      return;
    }
    try {
      localStorage.removeItem('selectedClinicId');
      localStorage.removeItem('selectedClinicTimestamp');
      clearActiveClinicId();
      await login(email, password);
      navigate('/', { replace: true });
    } catch {
      navigate('/login', { state: { email } });
    }
  }, [inviteDetails?.email, password, login, navigate]);

  const handleNext = async () => {
    const stepId = visibleSteps[currentStep]?.id;

    if (stepId === 'set-password' && setPasswordRef.current) {
      const ok = await setPasswordRef.current.triggerValidation();
      if (!ok) {
        toast({ title: 'Invalid password', description: 'Fix the errors and try again.', variant: 'destructive' });
        return;
      }
    }

    if (stepId === 'acknowledgement') {
      if (!isAcknowledged) {
        toast({ title: 'Agreement required', description: 'Please acknowledge the agreement to continue.', variant: 'destructive' });
        return;
      }
      if (!token) {
        toast({ title: 'Error', description: 'Invalid token', variant: 'destructive' });
        return;
      }
      setIsSubmitting(true);
      try {
        const response = await clinicOnboardingService.completePartialOnboarding({
          token,
          ...(skipPassword ? {} : { password }),
          program_acknowledged: true,
        });
        if (response.data?.onboardingToken) {
          setOnboardingToken(response.data.onboardingToken);
        }
        if (response.data?.billingPending) {
          setCurrentStep((prev) => prev + 1);
        } else {
          await runAutoAuth();
        }
      } catch (err: any) {
        toast({
          title: 'Error',
          description: err?.response?.data?.error?.message || 'Failed to complete setup.',
          variant: 'destructive',
        });
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    setCurrentStep((prev) => prev + 1);
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep((prev) => prev - 1);
  };

  const currentStepId = visibleSteps[currentStep]?.id;
  const isBillingStep = currentStepId === 'billing';
  const isStripeOnboardingStep = currentStepId === 'stripe-onboarding';

  const renderStep = () => {
    switch (currentStepId) {
      case 'set-password':
        return (
          <SetPasswordStep
            ref={setPasswordRef}
            ownerEmail={inviteDetails?.email || ''}
            password={password}
            confirmPassword={confirmPassword}
            onUpdate={({ password: p, confirm_password: c }) => {
              setPassword(p);
              setConfirmPassword(c);
            }}
          />
        );
      case 'acknowledgement':
        return (
          <AcknowledgementStep
            data={{
              programAcknowledged: isAcknowledged,
              clinicArchetype: inviteDetails?.clinicArchetype || 'guided',
            } as any}
            updateData={(u) => {
              if (u.programAcknowledged !== undefined) setIsAcknowledged(u.programAcknowledged);
            }}
            onAcknowledged={setIsAcknowledged}
          />
        );
      case 'billing':
        return (
          <BillingStep
            allowSkip={true}
            isGuided={(inviteDetails?.partialData as { clinic_archetype?: string })?.clinic_archetype === 'guided'}
            onSetupIntentRequest={async () => {
              const t = onboardingToken || token;
              if (!t) return null;
              const result = await clinicOnboardingBillingService.createSetupIntent(t);
              return result?.clientSecret ?? null;
            }}
            onPaymentMethodConfirmed={async (paymentMethodId) => {
              const t = onboardingToken || token;
              if (!t) throw new Error('No token');
              await clinicOnboardingBillingService.attachPaymentMethod(t, paymentMethodId);
              setCurrentStep((prev) => prev + 1);
            }}
            onSkip={async () => {
              const t = onboardingToken || token;
              if (!t) return;
              await clinicOnboardingBillingService.skipBilling(t);
              setCurrentStep((prev) => prev + 1);
            }}
          />
        );
      case 'stripe-onboarding':
        return (
          <StripeOnboardingStep
            onAccountSessionRequest={async () => {
              const t = onboardingToken || token;
              if (!t) return null;
              return clinicOnboardingBillingService.createStripeAccountSession(t);
            }}
            onComplete={runAutoAuth}
            onSkip={runAutoAuth}
          />
        );
      default:
        return null;
    }
  };

  const progress = ((currentStep + 1) / visibleSteps.length) * 100;

  // Loading state
  if (isValidating) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <motion.div
          className="flex flex-col items-center space-y-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <Loader2 className="h-8 w-8 animate-spin text-brand-teal" />
          <p className="text-gray-500 text-sm">Validating your link...</p>
        </motion.div>
      </div>
    );
  }

  // Error state
  if (validationError) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-4">
        <motion.div
          className="w-full max-w-sm"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex flex-col items-center mb-8">
            <img src="/nimbus_logo.svg" alt="Nimbus Healthcare" className="h-9" />
          </div>
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100 text-center">
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
              <XCircle className="h-6 w-6 text-red-500" />
            </div>
            <h1 className="text-lg font-semibold text-gray-900 mb-2">Invalid Link</h1>
            <p className="text-sm text-gray-500 mb-6 leading-relaxed">{validationError}</p>
            <Button
              onClick={() => navigate('/login')}
              className="w-full h-11 rounded-xl bg-brand-teal hover:bg-brand-teal/90 text-white font-medium shadow-md shadow-brand-teal/20 transition-all"
            >
              Go to Login
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-[680px] mx-auto px-4 py-8 lg:py-12">

          {/* Logo */}
          <motion.div
            className="flex flex-col items-center mb-10"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <img src="/nimbus_logo.svg" alt="Nimbus Healthcare" className="h-9" />
            <div className="mt-2.5 flex items-center">
              <div className="h-px w-6 bg-gray-300" />
              <span className="mx-2.5 text-[10px] font-semibold text-brand-teal uppercase tracking-[0.15em]">
                Clinic & Practitioner
              </span>
              <div className="h-px w-6 bg-gray-300" />
            </div>
          </motion.div>

          {/* Main Card */}
          <motion.div
            className="bg-white rounded-2xl shadow-lg border border-gray-100/80 overflow-hidden"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.5 }}
          >
            {/* Card Header — Teal gradient banner */}
            <div className="bg-gradient-to-r from-brand-teal to-[#2D6A78] px-6 py-5 sm:px-8 sm:py-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                  <Building2 className="h-4.5 w-4.5 text-white" />
                </div>
                <div>
                  <h1 className="text-base font-semibold text-white leading-tight">Complete Your Setup</h1>
                  {inviteDetails?.clinicName && (
                    <p className="text-sm text-white/70 mt-0.5">{inviteDetails.clinicName}</p>
                  )}
                </div>
              </div>

              {/* Stepper */}
              <div className="flex items-center">
                {visibleSteps.map((step, index) => {
                  const isActive = index === currentStep;
                  const isComplete = index < currentStep;
                  const Icon = step.icon;
                  const isLast = index === visibleSteps.length - 1;
                  return (
                    <React.Fragment key={step.id}>
                      <div className="flex items-center gap-2 shrink-0">
                        <div
                          className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 ${
                            isActive
                              ? 'bg-white text-brand-teal shadow-md'
                              : isComplete
                                ? 'bg-white/25 text-white'
                                : 'bg-white/10 text-white/40'
                          }`}
                        >
                          {isComplete ? (
                            <Check className="h-3.5 w-3.5 shrink-0" strokeWidth={3} />
                          ) : (
                            <Icon className="h-3.5 w-3.5 shrink-0" />
                          )}
                        </div>
                        <span className={`text-xs font-medium whitespace-nowrap hidden sm:inline transition-colors ${
                          isActive ? 'text-white' : isComplete ? 'text-white/70' : 'text-white/40'
                        }`}>
                          {step.title}
                        </span>
                      </div>
                      {!isLast && (
                        <div className={`flex-1 min-w-[12px] h-px mx-2 sm:mx-3 shrink-0 transition-colors ${
                          isComplete ? 'bg-white/40' : 'bg-white/15'
                        }`} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>

              {/* Progress bar */}
              <div className="mt-4 h-1 bg-white/15 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-white rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                />
              </div>
            </div>

            {/* Step Content */}
            <div className="px-6 py-6 sm:px-8 sm:py-8">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStepId}
                  initial={{ opacity: 0, x: 12 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -12 }}
                  transition={{ duration: 0.25 }}
                  className="min-h-[300px]"
                >
                  {renderStep()}
                </motion.div>
              </AnimatePresence>

              {!isBillingStep && !isStripeOnboardingStep && (
                <div className="flex items-center justify-between pt-6 mt-6 border-t border-gray-100">
                  <Button
                    variant="ghost"
                    onClick={handleBack}
                    disabled={currentStep === 0 || isSubmitting}
                    className="text-gray-500 hover:text-gray-700 hover:bg-gray-50 h-10 px-4 rounded-lg disabled:opacity-0"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1.5" />
                    Back
                  </Button>
                  <Button
                    onClick={handleNext}
                    disabled={isSubmitting}
                    className="h-10 px-6 rounded-lg bg-brand-teal hover:bg-brand-teal/90 text-white font-medium shadow-md shadow-brand-teal/20 hover:shadow-lg hover:shadow-brand-teal/25 transition-all"
                  >
                    {isSubmitting ? (
                      <><Loader2 className="h-4 w-4 animate-spin mr-2" />Creating...</>
                    ) : (
                      <>{currentStepId === 'acknowledgement' ? 'Continue' : 'Next'}<ArrowRight className="h-4 w-4 ml-1.5" /></>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </motion.div>

          {/* Footer area */}
          <div className="mt-6 flex flex-col items-center gap-3">
            <div className="flex items-center gap-1.5 text-gray-400 text-xs">
              <Shield className="w-3.5 h-3.5" />
              <span>Secured with enterprise-grade encryption</span>
            </div>
            <p className="text-xs text-gray-400">{getFooterText()}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartialClinicOnboarding;
